<template>
  <div class="card mb-2" ref="card">
    <div class="card-header bg-primary text-white">
      <span class="font-weight-bold">Property Details</span>
    </div>
    <div class="card-body">
      <form @submit.prevent class="needs-validation" novalidate autocomplete="off">
        <div v-if="serverError" class="alert alert-danger" role="alert">
          {{serverError}}
        </div>
        <div class="form-group row">
          <label class="col-lg-4 label-text">
            Property ID
          </label>
          <div class="col-lg-5">
            {{ property.id }}
          </div>
        </div>
        <div class="form-group row">
          <label for="propertyStatus" class="col-lg-4 label-text">
            <span class="text-danger">*</span>Property Status
          </label>
          <div class="col-lg-8">
            <LoadingSpinner v-if="propertyStatusLoading" message ='' size = '1x'>
            </LoadingSpinner>
            <select v-if="!propertyStatusLoading"
                    v-validate="'required'"
                    data-vv-as="Property Status"
                    class="form-control"
                    name="propertyStatus"
                    v-model="property.statusId">
              <option v-for="option in propertyStatusOptions"
                      :key="option.id"
                      :value="option.id">
              {{ option.name }}
              </option>
            </select>
            <div class="invalid-feedback">{{ errors.first('propertyStatus') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="propertyName" class="col-lg-4 col-form-label label-text">
            Property Name
          </label>
          <div class="col-lg-8">
            <input data-vv-as="Property Name"
                   type="text"
                   class="form-control"
                   name="propertyName"
                   v-model="property.name"
                   placeholder="Enter Property Name"
                   maxlength="50" />
            <small v-if="!errors.first('propertyName')" class="form-text text-muted">
              Max 50 characters
            </small>
            <div class="invalid-feedback">{{ errors.first('propertyName') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Calculated Acres
          </div>
          <div class="col-sm-8">
            {{currentProperty.calculatedAcres | acresFormatNullIsNA}}
          </div>
        </div>
        <div class="form-group row">
          <div class="col-sm-4 label-text">
            Divisions Allowed
          </div>
          <div class="col-sm-8">
            {{currentProperty.futurePropertiesAllowed | numberFormatNullIsNA}}
          </div>
        </div>
        <div class="form-group row">
          <label for="targetInspectionFrequency" class="col-lg-4 col-form-label label-text">
            Target Inspection Frequency (Years)
          </label>
          <div class="col-lg-8">
            <input v-validate="'numeric|min_value:1'"
                   data-vv-as="Target Inspection Frequency"
                   class="form-control"
                   name="targetInspectionFrequency"
                   v-model="property.targetInspectionFrequency"
                   placeholder="Enter Target Inspection Frequency" />
            <div class="invalid-feedback">{{ errors.first('targetInspectionFrequency') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <div class="col-lg-4 label-text">
            Primary Access Point
          </div>
        </div>
        <div class="form-group row">
          <label for="accessPointLatitude" class="col-lg-3 offset-lg-1 col-form-label label-text">
            Latitude
          </label>
          <div class="col-lg-8">
            <input v-validate="'between:36.43000,39.50000|decimal_between:4,5'"
                  data-vv-as="Primary Access Point Latitude"
                  class="form-control"
                  name="accessPointLatitude"
                  v-model="property.accessPointLatitude"
                  placeholder="Enter Primary Access Point Latitude" />
            <div class="invalid-feedback">{{ errors.first('accessPointLatitude') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="accessPointLongitude"
            class="col-lg-3 offset-lg-1 col-form-label label-text">
            Longitude
          </label>
          <div class="col-lg-8">
            <input v-validate="'between:-83.70000,-75.10000|decimal_between:4,5'"
                  data-vv-as="Primary Access Point Longitude"
                  class="form-control"
                  name="accessPointLongitude"
                  v-model="property.accessPointLongitude"
                  placeholder="Enter Primary Access Point Longitude" />
            <div class="invalid-feedback">{{ errors.first('accessPointLongitude') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="inspectWithTwoOrMoreStaff" class="col-lg-4 label-text">
            Inspect Property with Two or More Staff
          </label>
          <div class="col-lg-8">
            <b-form-checkbox
              v-model="property.inspectWithTwoOrMoreStaff"
              name="inspectWithTwoOrMoreStaff"
            >
            </b-form-checkbox>
          </div>
        </div>
        <div class="form-group row">
          <label for="inspectWithTwoOrMoreStaff" class="col-lg-4 label-text">
            Additional Interest Holder
          </label>
          <div class="col-lg-8">
            <b-form-checkbox
              v-model="property.additionalInterestHolder"
              name="additionalInterestHolder"
            >
            </b-form-checkbox>
          </div>
        </div>
        <div class="form-group row">
          <label for="propertyNotes" class="col-lg-4 label-text">
            Property Notes
          </label>
          <div class="col-lg-8">
            <textarea data-vv-as="Notes"
                      type="textarea"
                      class="form-control"
                      name="notes"
                      v-model="property.notes"
                      placeholder="Enter Notes"
                      rows="3"
                      maxlength="1000" />
              <small v-if="!errors.first('notes')" class="form-text text-muted">
                Max 1000 characters
              </small>
              <div class="invalid-feedback">{{ errors.first('notes') }}</div>
          </div>
        </div>
        <div
          v-if="property.legacyPropertyIds && property.legacyPropertyIds !== ''"
          class="form-group row">
          <div class="col-lg-4 label-text">
            Legacy Property ID(s)
          </div>
          <div class="col-lg-8">
            {{property.legacyPropertyIds}}
          </div>
        </div>
        <CancelModal
               modalId="cancelConfirmation"
               ref='cancelModal'
               @cancelled="cancelConfirmed">
        </CancelModal>
      </form>
    </div>
    <div class="card-footer right-align-content">
      <b-button :disabled="submitting"
        variant="danger"
        class="button-row-spacing"
        v-b-modal="'cancelConfirmation'"
      >
        Cancel
      </b-button>
      <b-button @click="submitWrapper(property)" :disabled="submitting">
        Save
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import CancelModal from '../CancelModal.vue';
import { LookupModules } from '@/store/moduleConfig';
import LoadingSpinner from '../LoadingSpinner.vue';
import FormSubmit from '@/mixins/FormSubmit';

export default {
  name: 'EditPropertyForm',
  mixins: [FormSubmit],
  data() {
    return {
      property: {},
    };
  },
  computed: {
    ...mapState(LookupModules.PROPERTY_STATUSES.namespace, {
      propertyStatusLoading: state => state.loading,
    }),
    ...mapState('Property', {
      currentProperty: state => state.currentProperty,
      serverError: state => state.serverError,
      submitting: state => state.submitting,
    }),
    ...mapGetters(LookupModules.PROPERTY_STATUSES.namespace, {
      propertyStatusOptions: 'getActiveOptions',
    }),
  },
  components: {
    CancelModal,
    LoadingSpinner,
  },
  mounted() {
    this.initialStatusId = this.currentProperty.statusId;
    Object.assign(this.property, this.currentProperty);
  },
  methods: {
    cancelConfirmed() {
      this.$store.dispatch('Property/stopEditing');
      this.stopEditing();
    },
    submitWrapper(property) {
      this.onSubmit(
        property,
        'Property',
        null,
        this.stopEditing,
      );
    },
    stopEditing() {
      this.$store.dispatch('Common/stopEditMode', 'Property');
      this.$refs.card.scrollIntoView();
    },
  },
};
</script>
